import type { ReactNode } from "react";
import { ArrowRight } from "lucide-react";

import { splitUnit } from "../format";

/**
 * What is live now, and what it would become.
 *
 * The single most common error on this surface is correcting the WRONG number,
 * and keeping both on screen is the cheapest guard against it (C3 interaction
 * rule 2). Both sides carry the unit: `899` and `899 kcal` are different claims,
 * and a diff that drops the unit on one side is a diff between two claims that
 * were never comparable.
 *
 * The approved mock-up makes this a CARD — a bordered box holding two columns,
 * each a small letter-spaced caption over a 26px figure — rather than two loose
 * lines joined by an arrow. The card is what makes the pair read as one
 * comparison: the arrow layout let the two halves drift apart at the pane's
 * width and left "Live now" looking like a heading over the whole pane.
 *
 * The unit is set at 15px regular beside the figure, so the number carries the
 * weight. The figure's accessible name is still the WHOLE label — "899 kcal" —
 * because a screen reader announcing "eight nine nine" and then "kcal" as two
 * unrelated fragments loses exactly the pairing this component exists to keep.
 *
 * Two draws of the same comparison, because the mock-up asks for two and the
 * claim must not fork between them:
 *   - `card`    — the pane. A bordered box, two columns, 26px figures.
 *   - `confirm` — the confirm dialog. A purple-tinted band, an arrow between the
 *                 halves, 24px figures, and the fact's identity set at the far
 *                 end so "which fact is this" is answered inside the same box
 *                 that answers "what is changing".
 * The arrow is available in `confirm` and nowhere else on purpose: it is legible
 * across a 640px dialog and it is exactly what fell apart at the pane's 460px.
 */
export function ValueDiff({
  current,
  next,
  unit = null,
  currentCaption = "Live now",
  nextCaption = "Your correction",
  currentNote = null,
  nextNote = null,
  variant = "card",
  trailing = null,
  currentLines = null,
  nextLines = null,
}: {
  current: string;
  /** `null` while nothing has been typed — the live value renders alone. */
  next: string | null;
  /** Set at 15px beside each figure. `null` for a fact that carries no unit. */
  unit?: string | null;
  currentCaption?: string;
  nextCaption?: string;
  /** The small line under a figure — who approved it, when a draft was saved. */
  currentNote?: string | null;
  nextNote?: string | null;
  variant?: "card" | "confirm";
  /** Set at the far end of the `confirm` band — which fact this is. */
  trailing?: ReactNode;
  /**
   * A COMPOUND value's parts, one per line, replacing the single big figure.
   *
   * An ingredient list is a real value with a real diff to check, but it is not
   * a figure: "C8 MCT Oil 16,975 mg · Arabic Gum 8,345 mg" set at 26px fills
   * the pane and still cannot be compared ingredient by ingredient. Given as
   * lines, the two sides stack in step and a changed amount sits directly
   * across from the one it replaces — which is the whole job of this card.
   *
   * The lines are passed in rather than split out of `current`/`next` here: an
   * ingredient name may contain the separator, and a diff that mis-splits one
   * name into two rows would show a change nobody made.
   */
  currentLines?: string[] | null;
  nextLines?: string[] | null;
}) {
  if (variant === "confirm") {
    return (
      <div className="flex flex-wrap items-center gap-4 rounded-ks-lg bg-ks-purple-4 p-4">
        <Side
          caption={currentCaption}
          value={current}
          lines={currentLines}
          unit={unit}
          note={currentNote}
          compact
        />
        {next !== null && (
          <>
            <ArrowRight aria-hidden className="size-[22px] shrink-0 text-ks-purple" />
            <Side
              caption={nextCaption}
              value={next}
              lines={nextLines}
              unit={unit}
              note={nextNote}
              emphasis
              compact
            />
          </>
        )}
        {trailing !== null && (
          <>
            <div className="grow" />
            <div className="text-right text-[13px] leading-[1.45] text-ks-ink">{trailing}</div>
          </>
        )}
      </div>
    );
  }

  return (
    <div
      className={`grid gap-3 rounded-ks-lg border border-ks-grey-4 p-4 ${
        next !== null ? "grid-cols-2" : "grid-cols-1"
      }`}
    >
      <Side
        caption={currentCaption}
        value={current}
        lines={currentLines}
        unit={unit}
        note={currentNote}
      />
      {next !== null && (
        <Side
          caption={nextCaption}
          value={next}
          lines={nextLines}
          unit={unit}
          note={nextNote}
          emphasis
        />
      )}
    </div>
  );
}

function Side({
  caption,
  value,
  lines = null,
  unit,
  note,
  emphasis = false,
  compact = false,
}: {
  caption: string;
  value: string;
  /** A compound value's parts. Replaces the figure — see `currentLines`. */
  lines?: string[] | null;
  unit: string | null;
  note: string | null;
  emphasis?: boolean;
  /** The confirm band's slightly smaller figure — 24px over 14px. */
  compact?: boolean;
}) {
  const parts = splitUnit(value, unit);
  const heading = (
    <p
      className={`text-[11px] font-bold tracking-[0.06em] uppercase ${
        emphasis ? "text-ks-purple" : "text-ks-grey-1"
      }`}
    >
      {caption}
    </p>
  );

  if (lines !== null) {
    return (
      <div className="flex min-w-0 flex-col gap-1">
        {heading}
        {/* The whole value as the accessible name, exactly as the figure does:
            a reader hearing the parts announced as loose fragments has lost the
            pairing this card exists to keep. */}
        <ul aria-label={value} className="flex flex-col gap-0.5">
          {lines.length === 0 ? (
            <li className="text-[15px] leading-snug text-ks-grey-1">No ingredients listed</li>
          ) : (
            // Keyed by position, not by text: this is a static, non-reorderable
            // read of one value, and two ingredients can legitimately render the
            // same line — a text key would collide and drop one of them.
            lines.map((line, at) => (
              <li
                key={at}
                className={`text-[15px] leading-snug font-bold break-words ${
                  emphasis ? "text-ks-ink" : "text-ks-grey-1"
                }`}
              >
                {line}
              </li>
            ))
          )}
        </ul>
        {note !== null && <p className="text-xs text-ks-grey-1">{note}</p>}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      {heading}
      {/* The whole label as the accessible name — see the component comment. */}
      <p
        aria-label={value}
        className={`leading-tight font-bold break-words ${compact ? "text-2xl" : "text-[26px]"} ${
          emphasis ? "text-ks-ink" : "text-ks-grey-1"
        }`}
      >
        {parts.value}
        {parts.unit !== null && (
          <span className={`font-normal ${compact ? "text-sm" : "text-[15px]"}`}>
            {" "}
            {parts.unit}
          </span>
        )}
      </p>
      {note !== null && <p className="text-xs text-ks-grey-1">{note}</p>}
    </div>
  );
}
